import type { LoadTrace } from '../services/mbrs/sheet-load-trace.service';
import { fetchSchemaAsset } from './schema-asset-fetch';
import { SHEET_LOAD_STEPS } from './sheet-load-trace.util';

/**
 * Thrown by {@link fetchSheetSchema} so a sheet's `catch` can tell a MISSING
 * form-definition file apart from an UNREADABLE one without re-inspecting the
 * response.
 *
 * The distinction matters operationally: `ASSET` points at the deployment (the
 * JSON did not ship, or the path is wrong, or a proxy answered with an HTML
 * error page), while `PARSE` points at the file's contents. Before this, both
 * produced the same "Failed to load form schema" toast because 24 of the 25
 * schema-fetching sheets never checked `response.ok` — a 404 that returns an
 * HTML body simply failed later, inside `response.json()`, and was reported as
 * if the JSON were corrupt.
 */
export class SchemaAssetError extends Error {
  constructor(
    /** Which half of the load failed — feeds the toast's diagnostic code. */
    readonly failure: 'NET' | 'ASSET' | 'PARSE',
    /** The asset path that was requested, for the console record. */
    readonly assetPath: string,
    message: string,
  ) {
    super(message);
    this.name = 'SchemaAssetError';
  }
}

/**
 * Fetch and parse one sheet's form-definition JSON, recording progress on the
 * sheet's load trace.
 *
 * This is the single seam through which all 26 role components load their
 * schema. It exists so that the `response.ok` check, the ASSET/PARSE split, and
 * the `schemaFetched` step mark are written once rather than copied 26 times —
 * the previous hand-rolled shape had drifted so far that exactly one sheet
 * (role 8) checked the response status at all.
 *
 * Marks {@link SHEET_LOAD_STEPS.schemaFetched} on a successful response. It
 * deliberately does NOT mark `schemaParsed`: sheets that preload cross-sheet
 * schemas for their equality-rule labels do that work immediately after this
 * call, and step 4 covers both. Marking it here would report a sheet as having
 * parsed its schema when a later preload was what actually failed.
 *
 * Uses native `fetch` rather than `HttpClient` — these are static assets served
 * from the app's own origin, and routing them through `HttpClient` would drag in
 * the CSRF and no-cache interceptors for no benefit.
 *
 * @typeParam TSchema The sheet's own schema shape. Each role component declares
 *                    its own local `FormSchema` interface, so this stays generic
 *                    rather than forcing a shared model on all of them.
 * @param trace     The sheet's live load trace.
 * @param assetPath Path to the JSON, e.g. `/assets/forms/role-8.form.json`.
 * @throws SchemaAssetError on a request that never completed (`NET`), a non-OK
 *         response (`ASSET`) or an unparseable body (`PARSE`).
 */
export async function fetchSheetSchema<TSchema>(
  trace: LoadTrace,
  assetPath: string,
): Promise<TSchema> {
  return fetchJsonAsset<TSchema>(assetPath, () => trace.mark(SHEET_LOAD_STEPS.schemaFetched));
}

/**
 * Fetch and parse one static JSON asset (`/assets/...json`), classifying every way
 * it can fail. This is the single seam for ALL the JSON files a filing page needs —
 * the form definitions, `types.dict.json`, `msic_options.json` and
 * `scenarios.config.json` — so they cannot drift apart the way the hand-rolled
 * `fetch` calls did (two of them swallowed every error and carried on with an
 * empty value, with no toast and no code).
 *
 * Classification (what a tester will see in the toast):
 * - `NET`   — the request never completed (offline, DNS, CORS, TLS).
 * - `ASSET` — the file could not be had: a non-OK status, OR a `200` that answered
 *             with an HTML page. The deployed nginx has no `location` for `.json`, so
 *             a MISSING asset falls through to `location /` and its
 *             `try_files … /index.html` answers `200 text/html`. Without the HTML
 *             check that reads as an unparseable body (`PARSE`) and points the
 *             reader at the file's contents when the file is simply not there.
 * - `PARSE` — the file arrived (non-HTML, 2xx) but its body is not valid JSON.
 *
 * @param assetPath Path to the JSON, e.g. `/assets/types.dict.json`.
 * @param onFetched Called once, after an acceptable answer and BEFORE the body is
 *                  parsed. `fetchSheetSchema` uses it to mark step 3 at the right
 *                  moment, so a PARSE still shows step 3 reached.
 * @throws SchemaAssetError for each of the three classes above.
 */
export async function fetchJsonAsset<TData>(
  assetPath: string,
  onFetched?: () => void,
): Promise<TData> {
  let response: Response;
  try {
    // Through the single asset-fetch seam so the QA simulate-failure switch can
    // stand in for the network (a bare `fetch` unless that is on).
    response = await fetchSchemaAsset(assetPath);
  } catch (networkError) {
    // `fetch` rejects only when the request never completed — offline, DNS,
    // CORS, TLS or an unreachable asset host. That is exactly what `NET` means
    // ("no response ever reached the browser"), so it is reported as NET and
    // the user is told to check their connection, not to contact an admin.
    // (It used to be ASSET, to keep it apart from the conversion's data call;
    // that made a sheet switch while offline read as a deployment fault. The
    // console record's `origin` and assetPath still say it was the schema.)
    // ASSET is now only a response that arrived non-OK.
    throw new SchemaAssetError(
      'NET',
      assetPath,
      `Request for ${assetPath} did not complete: ${String(networkError)}`,
    );
  }

  if (!response.ok) {
    throw new SchemaAssetError(
      'ASSET',
      assetPath,
      `Request for ${assetPath} answered ${response.status} ${response.statusText}`,
    );
  }

  // A 200 that is an HTML page is the host's SPA fallback standing in for a file
  // that is not there (see the docblock) — never a JSON asset.
  const contentType = response.headers?.get('content-type') ?? '';
  if (contentType.toLowerCase().includes('text/html')) {
    throw new SchemaAssetError(
      'ASSET',
      assetPath,
      `Request for ${assetPath} answered an HTML page (${contentType}) — the file is missing and the host fell back to index.html`,
    );
  }

  onFetched?.();

  try {
    return (await response.json()) as TData;
  } catch (parseError) {
    throw new SchemaAssetError(
      'PARSE',
      assetPath,
      `Body of ${assetPath} is not valid JSON: ${String(parseError)}`,
    );
  }
}

/**
 * Map whatever a sheet's init threw onto a failure class for the toast.
 *
 * Anything that is not a {@link SchemaAssetError} got past the schema load, so
 * by elimination it came from building the form or replaying saved state —
 * `BUILD`. That single inference is what lets roles 12 to 19, whose whole init
 * chain sits behind one `.catch`, still report where they actually died.
 */
export function classifySheetInitError(error: unknown): 'NET' | 'ASSET' | 'PARSE' | 'BUILD' {
  return error instanceof SchemaAssetError ? error.failure : 'BUILD';
}
